import { useState } from 'react'
import { Button, HStack, Text, VStack, chakra } from '@chakra-ui/react'
import { ResultStatus } from '../../../types/script'
import type { TurfContactDto } from '../../../types/canvasser'
import { ConfirmDeceased } from './ConfirmDeceased'

export type RollUpChoice = 'notHome' | 'moved' | 'deceased' | 'refused'
const OPTIONS: { value: RollUpChoice; label: string; needs: ResultStatus }[] = [
  { value: 'notHome', label: 'Not home', needs: ResultStatus.NoAnswer },
  { value: 'moved', label: 'Moved', needs: ResultStatus.Moved },
  { value: 'deceased', label: 'Deceased', needs: ResultStatus.Deceased },
  { value: 'refused', label: 'Refused', needs: ResultStatus.Refused },
]

/** [Spec §4.9] Everyone not talked to, defaulted to Not home; Done records them all in the door's batch. */
export function RollUpStep({ people, allowed, onDone, onTalkToSomeoneElse, isSaving }: {
  people: TurfContactDto[]; allowed: ReadonlySet<ResultStatus>
  onDone: (choices: Record<string, RollUpChoice>) => void; onTalkToSomeoneElse: () => void; isSaving: boolean
}) {
  const [choices, setChoices] = useState<Record<string, RollUpChoice>>(
    () => Object.fromEntries(people.map(p => [p.contactId, 'notHome' as RollUpChoice])))
  const [confirmFor, setConfirmFor] = useState<string | null>(null)
  const opts = OPTIONS.filter(o => allowed.has(o.needs))
  return (
    <VStack flex={1} align="stretch" gap={4} px={4} pb={6} overflowY="auto">
      <Text fontSize="xl" fontWeight="bold">Everyone else here?</Text>
      {people.map(p => (
        <VStack key={p.contactId} align="stretch" gap={2} borderBottomWidth="1px" borderColor="border" pb={3}>
          <Text fontWeight="semibold">{`${p.firstName} ${p.lastName ?? ''}`.trim()}</Text>
          <HStack gap={2} wrap="wrap" role="radiogroup" aria-label={p.firstName}>
            {opts.map(o => (
              <chakra.label key={o.value} display="flex" alignItems="center" gap={2} minH="44px" px={3} borderWidth="1px"
                borderRadius="full" borderColor={choices[p.contactId] === o.value ? 'accent' : 'border'}
                bg={choices[p.contactId] === o.value ? 'accent.soft' : 'surface'}>
                <input type="radio" name={`rollup-${p.contactId}`} aria-label={`${p.firstName}: ${o.label}`}
                  checked={choices[p.contactId] === o.value}
                  onChange={() => (o.value === 'deceased' ? setConfirmFor(p.contactId) : setChoices({ ...choices, [p.contactId]: o.value }))} />
                <Text fontSize="sm">{o.label}</Text>
              </chakra.label>
            ))}
          </HStack>
          {confirmFor === p.contactId && (
            <ConfirmDeceased name={p.firstName}
              onConfirm={() => { setChoices({ ...choices, [p.contactId]: 'deceased' }); setConfirmFor(null) }}
              onCancel={() => setConfirmFor(null)} />
          )}
        </VStack>
      ))}
      <Button minH="52px" bg="accent" color="accent.fg" disabled={isSaving} onClick={() => onDone(choices)}>Done → Next door</Button>
      <Button minH="48px" variant="outline" borderColor="border" onClick={onTalkToSomeoneElse}>Talk to someone else here?</Button>
    </VStack>
  )
}
